/**
 * Settings-specific React Query key constants.
 *
 * Generic keys (auth, views, nav) are now in @m3-tech/boss-api (apiQueryKeys).
 */

// ── Employee ──────────────────────────────────────────────────────────
export const EMPLOYEE_QUERY_KEY = ['employee', 'current'] as const

// ── Personal Settings ──────────────────────────────────────────────────
export const PERSONAL_SETTINGS_QUERY_KEY = ['personal-settings', 'current'] as const

// ── Personal Tax Settings ─────────────────────────────────────────────
export const PERSONAL_TAX_SETTINGS_QUERY_KEY = ['personal-tax-settings', 'current'] as const

// ── System Settings ────────────────────────────────────────────────────
export const SYSTEM_SETTINGS_QUERY_KEY = ['system-settings', 'current'] as const
export const SYNC_NOTIFICATION_QUERY_KEY = ['notification-settings', 'sync'] as const

// ── Templates ─────────────────────────────────────────────────────────
export const TEMPLATES_LIST_QUERY_KEY = ['templates', 'list'] as const
export const templateListQueryKey = <T>(params: T) => ['templates', 'list', params] as const
export const templateDetailQueryKey = (id?: string | number) => ['templates', 'detail', id] as const
export const TEMPLATE_TYPES_QUERY_KEY = ['templates', 'email-types'] as const
export const templateReminderQueryKey = (id?: string | number) =>
    ['templates', 'reminder', id] as const
export const templateStatusesQueryKey = (isTemplateForLa: boolean) =>
    ['templates', 'statuses', isTemplateForLa] as const
export const templateStatusPreferenceQueryKey = (id?: string | number) =>
    ['templates', 'status-preference', id] as const
export const TEMPLATE_IMAGES_QUERY_KEY = ['templates', 'images'] as const
// Normalize the id to a string so the query (keyed by the string `objectId`)
// and the mutation invalidations (which pass a numeric templateId) produce the
// SAME key — otherwise '42' !== 42 and invalidateQueries never matches.
export const emailTemplateAttachmentsQueryKey = (id?: string | number) =>
    ['templates', 'attachments', id != null ? String(id) : id] as const

// ── Task Templates ────────────────────────────────────────────────────
export const TASK_TEMPLATES_LIST_QUERY_KEY = ['task-templates', 'list'] as const
export const taskTemplateDetailQueryKey = (id?: string | number) =>
    ['task-templates', 'detail', id] as const
export const taskTemplateSearchQueryKey = (query: string, searchColumn: string, french: boolean) =>
    ['task-templates', 'search', query, searchColumn, french] as const
export const BROKER_TYPES_QUERY_KEY = ['broker-types'] as const
// View sharing (task templates are shared through their view — AB#2518)
export const VIEW_SHARE_QUERY_KEY = ['view-share'] as const
export const viewSharedWithQueryKey = (viewId: number) =>
    [...VIEW_SHARE_QUERY_KEY, 'shared-with', viewId] as const
export const viewShareBrandsQueryKey = (objectTypeId: number) =>
    [...VIEW_SHARE_QUERY_KEY, 'brands', objectTypeId] as const
export const viewShareCompaniesQueryKey = (objectTypeId: number, networkId: number) =>
    [...VIEW_SHARE_QUERY_KEY, 'companies', objectTypeId, networkId] as const
export const viewShareEmployeesQueryKey = (params: Record<string, number | undefined>) =>
    [...VIEW_SHARE_QUERY_KEY, 'employees', params] as const

// ── Commission Password ──────────────────────────────────────────────
export const COMMISSION_PASSWORD_QUERY_KEY = ['commission-password', 'status'] as const

// ── Security Settings ─────────────────────────────────────────────────
export const SECURITY_SETTINGS_QUERY_KEY = ['security-settings'] as const

// ── Integration Settings ──────────────────────────────────────────────
export const INTEGRATION_ACCESS_QUERY_KEY = ['integration-access', 'list'] as const

// ── Marketing Settings ────────────────────────────────────────────────
export const MARKETING_SETTINGS_QUERY_KEY = ['marketing-settings', 'current'] as const
// Keyed by the file hash so each photo is cached/fetched independently.
export const profilePhotoQueryKey = (fileHash: string | null) =>
    ['profile-photo', fileHash] as const

// ── Feature Management ────────────────────────────────────────────────
export const FEATURE_LIST_QUERY_KEY = ['features', 'list'] as const

// ── User Profile Feature Management ───────────────────────────────────
export const USER_PROFILE_LIST_QUERY_KEY = ['user-profiles', 'list'] as const
export const userProfileFeaturesQueryKey = (id: number | string) =>
    ['user-profiles', 'features', id] as const
export const userProfileConfigurationQueryKey = (identifier: string) =>
    ['user-profiles', 'configuration', identifier] as const

export const FEATURE_PROFILE_NETWORKS_QUERY_KEY = ['feature-profile', 'networks'] as const
export const FEATURE_PROFILE_AFFILIATES_QUERY_KEY = ['feature-profile', 'affiliates'] as const
export const FEATURE_PROFILE_BOSS_VERSIONS_QUERY_KEY = ['feature-profile', 'boss-versions'] as const
export const featureProfileProvincesQueryKey = (relatedTo: string, entityId: number | null) =>
    ['feature-profile', 'provinces', relatedTo, entityId] as const

// ── Gross Commission Visibility ──────────────────────────────────────
export const GROSS_COMMISSION_QUERY_KEY = ['gross-commission', 'list'] as const

// ── Mopolo Settings ─────────────────────────────────────────────────
export const MOPOLO_SETTINGS_QUERY_KEY = ['mopolo-settings', 'current'] as const

// ── Mailchimp ───────────────────────────────────────────────────────
export const MAILCHIMP_CONNECTION_QUERY_KEY = ['mailchimp', 'connection'] as const

// ── Website Settings ────────────────────────────────────────────────
export const WEBSITE_SETTINGS_QUERY_KEY = ['website-settings', 'current'] as const
export const adminEmployeeWebsiteSettingsQueryKey = (employeeId: number) =>
    ['website-settings', 'admin', 'employee', employeeId] as const
export const WEBSITE_OPTOUTS_QUERY_KEY = ['website-optouts', 'list'] as const
export const adminWebsiteOptoutsQueryKey = (empId: number) =>
    ['website-optouts', 'admin', empId] as const
export const adminOfficeWebsiteOptoutsQueryKey = (brand: string) =>
    ['website-optouts', 'admin', 'office', brand] as const
export const officeWebsiteOptoutsQueryKey = (brand: string) =>
    ['website-optouts', 'office', brand] as const
export const adminOfficeQueryKey = (officeId: number) => ['admin', 'office', officeId] as const
export const adminFranchiseQueryKey = (franchiseId: number) =>
    ['admin', 'franchise', franchiseId] as const
// Self-service (non-admin) office/brokerage settings for the /website switcher.
export const officeSettingsQueryKey = (officeId: number) => ['office-settings', officeId] as const
export const franchiseSettingsQueryKey = (franchiseId: number) =>
    ['franchise-settings', franchiseId] as const
export const adminFranchiseWebsiteOptoutsQueryKey = (brand: string) =>
    ['website-optouts', 'admin', 'brokerage', brand] as const
export const franchiseWebsiteOptoutsQueryKey = (brand: string) =>
    ['website-optouts', 'brokerage', brand] as const
export const BROKER_LICENSE_CITIES_QUERY_KEY = ['broker-license-cities', 'all'] as const
export const BROKER_LICENSE_REGIONS_QUERY_KEY = ['broker-license-regions', 'all'] as const
export const adminSearchQueryKey = (type: string, query: string) =>
    ['admin', 'search', type, query] as const

// ── Preset Commission ──────────────────────────────────────────────────
export const PRESET_COMMISSION_TEAM_QUERY_KEY = ['preset-commission', 'team'] as const

// ── Admin Preset Commission ────────────────────────────────────────────
export const ADMIN_BRAND_NAMES_QUERY_KEY = ['admin', 'brand', 'names'] as const
export const adminBrandCompaniesQueryKey = (networkID: number | string) =>
    ['admin', 'brand', 'companies', networkID] as const
export const adminBrandTeamLeadsQueryKey = (params: Record<string, unknown>) =>
    ['admin', 'brand', 'team-leads', params] as const
export const preCheckedAgentsQueryKey = (dealOwnerID: number | string) =>
    ['preset-commission', 'prechecked-agents', dealOwnerID] as const

// ── Broker Rates ────────────────────────────────────────────────────
export const BROKER_RATES_QUERY_KEY = ['broker-rates', 'list'] as const

// ── Brand Rates (Admin) ─────────────────────────────────────────────
export const BRAND_RATES_QUERY_KEY = ['brand-rates', 'list'] as const

// ── New Preset Commission ─────────────────────────────────────────────
export const PRE_CHECKED_AGENTS_QUERY_KEY = ['preset-commission', 'pre-checked-agents'] as const
export const commissionVisibilityQueryKey = (divisionId: number) =>
    ['commission-visibility', divisionId] as const
export const divisionARSelectionQueryKey = (divisionId: number) =>
    ['division-ar-selection', divisionId] as const

// ── Corporate Content (Admin Website Opt-Outs) ──────────────────────
export const CORPORATE_CONTENT_QUERY_KEY = ['corporate-content', 'list'] as const

// ── Land Transfer Tax (Admin) ────────────────────────────────────────
export const LAND_TRANSFER_TAX_QUERY_KEY = ['land-transfer-tax', 'list'] as const

// ── Auto Communication ──────────────────────────────────────────────
export const MARKETING_AC_SETTINGS_QUERY_KEY = ['marketing-ac-settings', 'list'] as const
export const MARKETING_ACMA_SETTINGS_QUERY_KEY = ['marketing-acma-settings', 'current'] as const
export const COMMUNICATION_PROGRAMS_QUERY_KEY = ['communication-programs', 'list'] as const
export const IMPACT_COMMUNICATION_QUERY_KEY = ['impact-communication', 'list'] as const

// ── Admin Programs ──────────────────────────────────────────────────
export const adminEmployeeProgramRegistrationsQueryKey = (employeeId: number) =>
    ['admin-employee-program-registrations', employeeId] as const
export const ADMIN_PROGRAM_TYPES_QUERY_KEY = ['admin-program-types'] as const
export const adminProgramActivityLogQueryKey = (employeeId: number, programId: number) =>
    ['admin-program-activity-log', employeeId, programId] as const

// ── Custom Signature ────────────────────────────────────────────────
export const SIGNATURES_LIST_QUERY_KEY = ['signatures', 'list'] as const
// Full (unfiltered) signature list for the email-template block manager —
// distinct key from SIGNATURES_LIST_QUERY_KEY, which caches a custom-only filter.
export const EMAIL_SIGNATURES_LIST_QUERY_KEY = ['signatures', 'list', 'all'] as const

// ── Data Transformation (admin) ─────────────────────────────────────
export const DATA_TRANSFORMATION_EMPLOYEES_QUERY_KEY = [
    'data-transformation',
    'available-employees',
] as const

// ── Data Transformation (admin) ─────────────────────────────────────
/** The Import_Type table, fetched once for the step-1 dropdown. */
export const IMPORT_TYPES_QUERY_KEY = ['data-transformation', 'import-types'] as const

// ── Data Import Summary (admin) ─────────────────────────────────────
// Both endpoints are server-paginated, so the page number and size are part of
// the key — a cached page must not be served for a different page. The bare
// list key is the invalidation target after Stop Import: it prefix-matches
// every cached page, so whichever page the user is on gets refetched.
export const DATA_IMPORT_SUMMARY_LIST_QUERY_KEY = ['data-import-summary', 'list'] as const
export const dataImportSummaryQueryKey = (pageNumber: number, pageSize: number) =>
    ['data-import-summary', 'list', pageNumber, pageSize] as const
export const dataImportSummaryDetailQueryKey = (id: number, pageNumber: number, pageSize: number) =>
    ['data-import-summary', 'detail', id, pageNumber, pageSize] as const

// ── Broker Domain (external API) ───────────────────────────────────
export const DOMAIN_QUERY_KEY = ['broker-domain'] as const
export const domainQueryKey = (userId: number) => ['broker-domain', userId] as const

// ── QA Testing ──────────────────────────────────────────────────────
export const QA_ACTIVE_COMMUNICATIONS_RUN_DATES_QUERY_KEY = [
    'qa-testing',
    'active-communications-run-dates',
] as const
export const QA_AUTOMATED_COMMUNICATIONS_QUERY_KEY = [
    'qa-testing',
    'automated-communications',
] as const
export const QA_INSURANCE_SYNCS_QUERY_KEY = ['qa-testing', 'insurance-syncs'] as const
export const qaEpicOptoutLogsQueryKey = (opportunities: string | null) =>
    ['qa-testing', 'epic-optout-logs', opportunities] as const

// ── Error Management (Admin diagnostic screen) ──────────────────────
export const ERROR_MANAGEMENT_DNS_QUERY_KEY = ['error-management', 'dns'] as const

// ── Task Template Editor (workflow triggers + merge-tag picker) ──────
export const WORKFLOW_STATUSES_QUERY_KEY = ['workflow-statuses', 'list'] as const
export const emailTemplateFieldsQueryKey = (objectTypeId: number | string) =>
    ['email-template-fields', String(objectTypeId)] as const
// Per-employee feature tree for the dynamic module-link picker.
export const EMPLOYEE_FEATURES_QUERY_KEY = ['employee-features', 'tree'] as const

// ── Dev Service Status (internal diagnostic screen) ──────────────────
export const DEV_SERVICE_STATUS_NOTIFICATIONS_QUERY_KEY = [
    'dev-service-status',
    'notifications',
] as const
export const DEV_SERVICE_STATUS_CREDITOR_USER_COUNT_QUERY_KEY = [
    'dev-service-status',
    'creditor-user-count',
] as const
// One SimProcessLog series per (insuranceType, province) chart, and the
// 10/30-day toggle refetches with a different recordCount — all three vary the
// request, so all three must be in the key or the five charts collide in cache.
export const devServiceStatusSimProcessLogQueryKey = (
    insuranceType: number,
    province: string,
    recordCount: number,
) => ['dev-service-status', 'sim-process-log', insuranceType, province, recordCount] as const

// ── Business Central — Transactions to review (FF-2856 / FF-2857) ─────
// Everything shares the `bc-needs-attention` root so a resend can invalidate
// the counts, both tables, the reasons and the open transaction in one call.
export const BC_NEEDS_ATTENTION_QUERY_KEY = ['bc-needs-attention'] as const
export const BC_NEEDS_ATTENTION_COUNTS_QUERY_KEY = [
    ...BC_NEEDS_ATTENTION_QUERY_KEY,
    'counts',
] as const
export const bcTransactionsQueryKey = (table: string, status?: string, group?: string) =>
    [...BC_NEEDS_ATTENTION_QUERY_KEY, 'list', table, status ?? 'all', group ?? 'all'] as const
export const bcReasonsQueryKey = (table: string) =>
    [...BC_NEEDS_ATTENTION_QUERY_KEY, 'reasons', table] as const
export const bcTransactionQueryKey = (id: number) =>
    [...BC_NEEDS_ATTENTION_QUERY_KEY, 'transaction', id] as const
export const bcTransactionHistoryQueryKey = (id: number) =>
    [...BC_NEEDS_ATTENTION_QUERY_KEY, 'history', id] as const
export const bcReferenceQueryKey = (reference: string) =>
    [...BC_NEEDS_ATTENTION_QUERY_KEY, 'reference', reference] as const
// Vendors are a live search of Business Central, not BOSS data — kept outside
// the root so a resend does not throw away the picker's results.
export const bcVendorSearchQueryKey = (id: number, search: string) =>
    ['bc-vendor-search', id, search] as const
